import {
  AfterViewInit,
  Component,
  Inject,
  Injector,
  OnInit,
  Optional,
  ViewChild,
  ChangeDetectionStrategy
} from "@angular/core";
import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { Control } from "src/app/models/control.model";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import { ControlService } from "src/app/services/control.service";
import nameOf from "src/app/shared/form/nameOf.helper";
import { CoreGridComponent } from "src/app/shared/grid/core-grid/core-grid.component";
import { ControlDialogComponent } from "src/app/modals/control-dialog/control-dialog.component";
import { IFormDialogData } from "src/app/shared/form/form.component";
import { DeleteDialog } from "src/app/shared/delete-dialog/delete-dialog.component";
import { ControlStatusEnum } from "src/app/enums/control-status.enum";
import { ControlTypeEnum } from "src/app/enums/control-type.enum";
import { AuthService } from "src/app/services";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { segsRouteConfig } from "src/app/shared/constants/segsRouteConfig.constants";
import { forkJoin, Observable, of } from "rxjs";
import { GridViewService } from "src/app/services/grid-view.service";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { Lookup } from "src/app/models/lookup.model";
import {
    ChildColumnConfig,
    ColumnConfig,
} from "src/app/shared/grid/core-grid/columnConfig.model";
import { ControlHierarchyBucketService } from "src/app/services/controlHierarchyBucket.service";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { UserService } from "src/app/services/user.service";
import { ILookup } from "src/app/shared/form/lookup.model";
import { ControlDetailsPopupComponent } from "src/app/common-components/control-details-popup/control-details-popup.component";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";
import { ISearchFieldData } from "src/app/shared/grid/baseGrid.component";
import { StatusLabels } from "src/app/shared/constants/status.constants";
import { ControlToAgentDialogComponent } from "src/app/modals/control-to-agent-dialog/control-to-agent-dialog.component";
import { TranslateService } from "src/app/services/translate.service";

@Component({
  standalone: false,
    selector: "app-controls-list",
    templateUrl: "./../../shared/grid/core-grid/core-grid.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        "./../../shared/grid/core-grid/core-grid.component.scss",
        "./controls-list.component.scss",
    ],
})
export class ControlsListComponent
    extends CoreGridComponent<Control>
    implements OnInit, AfterViewInit
{
    @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;
    controlHierarchyBuckets: Lookup[] = [];
    users: ILookup[] = [];

    constructor(
        protected controlService: ControlService,
        toastService: ToastService,
        commonService: CommonService,
        dialog: MatDialog,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private controlHierarchyBucketService: ControlHierarchyBucketService,
        private dateFormatService: DateFormatService,
        protected injector: Injector,
        private userService: UserService,
        private translateService: TranslateService,
        @Optional()
        @Inject(MAT_DIALOG_DATA)
        public searchFieldData: ISearchFieldData,
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
        );
        this.gridService = controlService;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    protected get objectId(): string {
        return RolePermissionObjects.Control;
    }

    ngOnInit(): void {
        this.searchDialogRef = this.injector.get(
            MatDialogRef<ControlsListComponent>,
            null,
        );
        super.ngOnInit();
        forkJoin({
            controlHierarchyBuckets:
                this.controlHierarchyBucketService.getAll(),
            users: this.userService.getAll(),
        }).subscribe({
            next: (res) => {
                this.controlHierarchyBuckets = res.controlHierarchyBuckets;
                this.users = res.users;
            },
            error: (error) => {
                console.error("Error loading lookup data:", error);
            },
        });
        this.startGridViewInitialization();
    }

    get hasOrgFilter(): boolean {
        return true;
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    override initializeProperties(): void {
        this.addEntityTitle = "Control";
        this.searchPlaceholder = "Search Controls";
    }

    override initializeColumns(): void {
        this.additionalActions = [];
        const translation = (text: string) =>
            this.translateService.getDynamicTranslation(text);
        this.columnConfigs = [
            {
                name: nameOf<Control>((x) => x.name),
                displayName: "Control Name",
                cssClass: () => "left-aligned cursor-pointer",
                headerCssClass: () => "left-aligned",
                onClick: (row: Control) => {
                    this.openFlyoutInternal(
                        ControlDetailsPopupComponent,
                        row,
                        "80vw",
                        true,
                    );
                },
            },
            {
                name: nameOf<Control>((x) => x.code),
                displayName: "Code",
            },
            {
                name: nameOf<Control>((x) => x.controlType),
                displayName: "Control Type",
                cellTemplate: (row) =>
                    translation(ControlTypeEnum[row.controlType]),
                enumFilterMap: this.enumToValueNameArray(ControlTypeEnum),
            },
            {
                name: nameOf<Control>((x) => x.ownerName),
                displayName: "Control Owner",
                cellTemplate: (row) => row.ownerName || "-",
            },
            new ColumnConfig<Control>({
                name: nameOf<Control>((x) => x.status),
                displayName: "Status",
                cellTemplate: () => "",
                enumFilterMap: this.enumToValueNameArray(ControlStatusEnum),
                children: (row) => {
                    const statusClass = this.getStatusCssClass(row.status);
                    return [
                        new ChildColumnConfig<Control>({
                            cellTemplate: () =>
                                translation(ControlStatusEnum[row.status]),
                            cssClass: () => `status-badge ${statusClass}`,
                            onClick: () => this.openStatusDropdown(row),
                        }),
                    ];
                },
            }),
            {
                name: nameOf<Control>((x) => x.controlHierarchyBucketId),
                displayName: "Control Bucket",
                cellTemplate: (row) =>
                    this.controlHierarchyBuckets?.find(
                        (b) => b.id === row.controlHierarchyBucketId,
                    )?.name ?? "-",
            },
            {
                name: nameOf<Control>((x) => x.notes),
                displayName: "Notes",
                cellTemplate: (row) => row.notes || "-",
            },
            {
                name: nameOf<Control>((x) => x.isActive),
                displayName: StatusLabels.active,
                cellTemplate: (row) => (row.isActive ? "Yes" : "No"),
            },
            {
                name: nameOf<Control>((x) => x.createdByUserId),
                displayName: "Created By",
                cellTemplate: (row) =>
                    this.users?.find((u) => u.id === row.createdByUserId)
                        ?.name || "-",
            },
            {
                name: nameOf<Control>((x) => x.updatedByUserId),
                displayName: "Updated By",
                cellTemplate: (row) =>
                    this.users?.find((u) => u.id === row.updatedByUserId)
                        ?.name || "-",
            },
            {
                name: nameOf<Control>((x) => x.dateCreated),
                displayName: "Created Date",
                cellTemplate: (row) =>
                    row.dateCreated
                        ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                              row.dateCreated.toString(),
                              this.dateFormatService.userDateFormat,
                          )
                        : "-",
            },
            {
                name: nameOf<Control>((x) => x.dateUpdated),
                displayName: "Date Updated",
                cellTemplate: (row) =>
                    row.dateUpdated
                        ? this.dateFormatService.convertUTCDateStringToUserTimeZoneString(
                              row.dateUpdated.toString(),
                              this.dateFormatService.userDateFormat,
                          )
                        : "-",
            },
            {
                name: nameOf<Control>((x) => x.controlAgents),
                displayName: "Agents",
                cellTemplate: (row: Control) => {
                    const agentCount = row.controlAgents?.length || 0;
                    return agentCount > 0
                        ? `${agentCount} ${translation("Agent" + (agentCount > 1 ? "s" : ""))}`
                        : translation("Add Agents");
                },
                cssClass: (row: Control) =>
                    (row.controlAgents?.length || 0) > 0
                        ? "agents-link view-agents"
                        : "agents-link add-agents",
                onClick: (row: Control) => {
                    this.manageControlAgents(row);
                },
            },
            { name: "action", displayName: "Actions" },
        ];
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<Control>((x) => x.controlHierarchyBucketId):
                return this.controlHierarchyBucketService;
            case nameOf<Control>((x) => x.createdByUserId):
                return this.userService;
            case nameOf<Control>((x) => x.updatedByUserId):
                return this.userService;
            default:
                return undefined;
        }
    }

    openStatusDropdown(control: Control): void {
        this.openEntityDialog(control.id);
    }

    getStatusCssClass(status: ControlStatusEnum): string {
        switch (status) {
            case ControlStatusEnum.Planned:
                return "status-planned";
            case ControlStatusEnum.Ongoing:
                return "status-ongoing";
            case ControlStatusEnum.Implemented:
                return "status-implemented";
            case ControlStatusEnum.Retired:
                return "status-retired";
            default:
                return "";
        }
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [];
    }

    override initializeTabs() {
        return segsRouteConfig;
    }

    override getOverviewData(): Observable<void> {
        return of(undefined);
    }

    override openEntityDialog(controlId?: string): void {
        const controlModel: IFormDialogData = {
            id: controlId ?? '',
        };
        this.openDialog(ControlDialogComponent, controlModel, false, "1000px");
    }

    override openEntityDeleteDialog(control: Control): void {
        this.openDeleteDialog(
            DeleteDialog,
            control,
            this.controlService,
            "Control",
        );
    }

    manageControlAgents(control: Control): void {
        const dialogData = {
            id: control.id,
            name: control.name,
            code: control.code,
            controlAgents: control.controlAgents,
        };
        const dialogRef = this.dialog.open(ControlToAgentDialogComponent, {
            data: dialogData,
            width: "800px",
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.loadPage();
            }
        });
    }
}
